import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

export interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  meta?: boolean;
  shift?: boolean;
  alt?: boolean;
  description: string;
  action: () => void;
  category?: string;
}

interface UseKeyboardShortcutsOptions {
  shortcuts: KeyboardShortcut[];
  enabled?: boolean;
}

/**
 * Hook to manage keyboard shortcuts
 * Handles registration, deregistration, and execution
 */
export function useKeyboardShortcuts({ 
  shortcuts, 
  enabled = true 
}: UseKeyboardShortcutsOptions) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // Don't trigger shortcuts when typing in inputs
      const target = event.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.contentEditable === "true"
      ) {
        // Allow some shortcuts even in inputs (like Escape)
        if (event.key !== "Escape") {
          return;
        }
      }

      for (const shortcut of shortcuts) {
        const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase();
        const ctrlMatches = shortcut.ctrl ? event.ctrlKey : !event.ctrlKey;
        const metaMatches = shortcut.meta ? event.metaKey : !event.metaKey;
        const shiftMatches = shortcut.shift ? event.shiftKey : !event.shiftKey;
        const altMatches = shortcut.alt ? event.altKey : !event.altKey;

        if (keyMatches && ctrlMatches && metaMatches && shiftMatches && altMatches) {
          event.preventDefault();
          shortcut.action();
          break;
        }
      }
    },
    [shortcuts, enabled]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);
}

/**
 * Global keyboard shortcuts
 * These are available throughout the app
 */
export function useGlobalKeyboardShortcuts() {
  const navigate = useNavigate();

  const shortcuts: KeyboardShortcut[] = [
    // Navigation shortcuts (G + key)
    {
      key: "d",
      description: "Go to Dashboard",
      action: () => navigate("/dashboard"),
      category: "Navigation",
    },
    {
      key: "a",
      description: "Go to Analytics",
      action: () => navigate("/dashboard/analytics"),
      category: "Navigation",
    },
    {
      key: "f",
      description: "Go to Feedback",
      action: () => navigate("/dashboard/feedback"),
      category: "Navigation",
    },
    {
      key: "p",
      description: "Go to Pages",
      action: () => navigate("/dashboard/pages"),
      category: "Navigation",
    },
    {
      key: "s",
      description: "Go to Settings",
      action: () => navigate("/dashboard/settings"),
      category: "Navigation",
    },
    
    // Search shortcut
    {
      key: "/",
      description: "Focus search",
      action: () => {
        const searchInput = document.querySelector<HTMLInputElement>('[type="search"]');
        searchInput?.focus();
      },
      category: "Search",
    },
    
    // Escape to close modals
    {
      key: "Escape",
      description: "Close modal/dialog",
      action: () => {
        // Escape key is handled by individual components
        // This is here for documentation purposes
      },
      category: "General",
    },
  ];

  useKeyboardShortcuts({ shortcuts });
  
  return shortcuts;
}

/**
 * Format keyboard shortcut for display
 */
export function formatShortcut(shortcut: KeyboardShortcut): string {
  const parts: string[] = [];
  
  if (shortcut.meta || shortcut.ctrl) {
    parts.push(navigator.platform.includes("Mac") ? "⌘" : "Ctrl");
  }
  if (shortcut.shift) parts.push("⇧");
  if (shortcut.alt) parts.push("⌥");
  
  parts.push(shortcut.key.toUpperCase());
  
  return parts.join(" + ");
}
